import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useConsole } from '@/app/useConsole'
import { holdsAnyRole } from '@/lib/auth/session'
import { useSession } from '@/lib/auth/useSession'
import {
  liveInspector, loaderFor, openerFor, readDecision, readScreen, readStampOf,
  type DataSource, type DecisionCheck,
} from '@/lib/data'
import type { CellRole, ConsoleConfig } from '@/lib/env/config'

import { Block } from './blocks'
import {
  COMMANDS,
  DEGRADED_NOTE,
  type Incident,
} from './fixtures'
import { ROLES, type ScreenState } from './laws'
import { NAV, labelOf } from './nav'
import { REACHABLE_NAV, isReachable, notBuiltBlocks, notBuiltReason } from './reachable'
import { CellBar, IncidentDrawer } from './shell/CellBar'
import { Inspector } from './shell/Inspector'
import { BreakGlassBanner, MastHead, StandbyBanner } from './shell/MastHead'
import { NavRail } from './shell/NavRail'
import { HelpOverlay, Palette, Toast, type PaletteGroup, type PaletteHit } from './shell/Palette'
import { ScreenHeader } from './shell/ScreenHeader'
import { DeniedView, EmptyView, ErrorView, LoadingView } from './shell/StateViews'
import { DEFAULT_INSP } from './spec/inspector'
import { SCREENS, screenFor } from './spec'
import type { Block as BlockSpec, NoteBlock } from './spec/types'

/**
 * The console.
 *
 * One component owns the session-shaped state because the prototype does, and
 * because these pieces are genuinely coupled: the role decides whether a
 * screen renders at all, the cell role decides whether its controls are live,
 * the search string filters both the palette and every table on screen, and
 * the keyboard map has to reach all of it. Splitting that into contexts would
 * be tidier to look at and harder to reason about.
 *
 * Screen selection lives in the URL. The design says every filter state is in
 * the URL and it means it — an operator hands a colleague a link to the exact
 * queue they are looking at, not a description of how to get there.
 */

interface Props {
  readonly config: ConsoleConfig
}

export function Console({ config }: Props) {
  const params = useParams<{ screenId?: string }>()
  const navigate = useNavigate()
  const { api } = useConsole()
  // Same query the masthead reads, so the strip and the actor cannot disagree
  // about who is signed in or which cell answered them.
  const session = useSession()

  const screenId = params.screenId !== undefined && params.screenId in SCREENS
    ? params.screenId
    : 'dashboard'
  const screen = screenFor(screenId)

  // The registry decides, per screen, whether this console is reading a backend
  // or is not built. Absent from it is the default and it is the safe one: a
  // screen nobody wired renders a stated not-built notice, whereas a default of
  // "live" would render fixtures and claim they are real.
  //
  // It USED to draw the design corpus here and badge it. That was honest while
  // the console was plainly a scaffold and stopped being so as the live count
  // grew — see ./reachable.ts for the argument. The corpus is still the design
  // contract and is still what the screens are built against; it is simply no
  // longer rendered as though it were operational data.
  //
  // These screens are also no longer in the rail, so reaching one takes a deep
  // link, a stale bookmark or a pasted URL. That is exactly when an honest
  // answer matters most: the operator arrived deliberately and is owed a reason
  // rather than a blank.
  const loader = loaderFor(screenId)
  const isLive = loader !== undefined

  const screenRead = useQuery({
    queryKey: ['screen', screenId],
    queryFn: () => {
      // `enabled` already prevents this; the guard is for the type system and
      // for whoever eventually edits that line out.
      if (loader === undefined) throw new Error(`no live loader for ${screenId}`)
      return readScreen(api, loader)
    },
    enabled: isLive,
    // A loader resolves for every answer the backend gives, including "I could
    // not read that" — so a rejection here is an abort or a bug, and neither
    // gets better on the second attempt. Retrying would only delay the state
    // views below, which are the honest rendering of not knowing.
    retry: false,
  })

  const [view, setView] = useState<ScreenState>('populated')
  const [tab, setTab] = useState(0)
  const [cursor, setCursor] = useState(0)
  const [inspTab, setInspTab] = useState(0)
  const [rawOpen, setRawOpen] = useState(false)

  const [theme, setTheme] = useState<'light' | 'dark'>('light')
  const [density, setDensity] = useState<34 | 44>(34)
  /**
   * The role being PREVIEWED, or `null` for "act as myself".
   *
   * It used to be `useState('Ops')` — a picked string that decided what every
   * operator could see, starting as Ops for a session holding only compliance
   * and for a session holding nothing at all. The dropdown's value could not
   * even be one of its own options.
   */
  const [rolePreview, setRolePreview] = useState<string | null>(null)
  /**
   * The cell role being DRAWN, and which of the three sources it came from.
   *
   * Precedence, and the order is the argument: a preview the operator asked
   * for, else the role the backend that answered reported, else the role this
   * bundle was configured with. Config is last because it is a claim about
   * where the console points rather than a fact about who replied — the failure
   * law 18 exists to catch is a bundle confidently naming a cell it is not
   * talking to, and a banner sourced from config cannot catch it.
   *
   * Part I §4 is unchanged: the config is still required and the app still
   * refuses to start without it. It stops being the last word and becomes a
   * claim that gets checked.
   */
  const [cellPreview, setCellPreview] = useState<CellRole | null>(null)
  const [environment, setEnvironment] = useState<string>(config.environment)
  const degraded = false
  const [breakGlass, setBreakGlass] = useState(false)

  const [navCollapsed, setNavCollapsed] = useState(false)
  const [inspOpen, setInspOpen] = useState(true)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [incidentsOpen, setIncidentsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [toast, setToast] = useState('')
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})
  const [facetsOn, setFacetsOn] = useState<ReadonlySet<string>>(() => new Set())

  const go = useCallback(
    (id: string) => {
      void navigate(`/${id}`)
      setView('populated')
      setTab(0)
      setCursor(0)
      setPaletteOpen(false)
      setQuery('')
    },
    [navigate],
  )

  const flash = useCallback((message: string) => {
    setToast(message)
  }, [])

  useEffect(() => {
    if (toast === '') return undefined
    const t = setTimeout(() => setToast(''), 2200)
    return () => clearTimeout(t)
  }, [toast])

  // The guide keys its palette off data-k and its row height off --k-row.
  useEffect(() => {
    document.documentElement.dataset['k'] = theme
    document.documentElement.style.setProperty('--k-row', `${density}px`)
    document.body.dataset['k'] = theme
  }, [theme, density])


  /** What the backend answered, or `null` until it has. */
  const answer = screenRead.data ?? null

  // On a live screen the corpus is not a fallback — it is not reachable at all.
  // An empty list while the read is in flight or failed is the point: the
  // states below draw that case, and the one thing that must never happen is a
  // fixture count standing in for a figure the backend did not give us.
  const blocks: readonly BlockSpec[] = isLive ? (answer?.data.blocks ?? []) : notBuiltBlocks(screenId)

  const provenance: DataSource | null = isLive
    ? (answer?.data.provenance ?? null)
    : { kind: 'not_built', screen: screenId, reason: notBuiltReason(screenId) }

  /**
   * What the screen calls itself, and what it says it is looking at.
   *
   * Neither comes from the corpus any more, and that is the whole point. The
   * spec gives every screen a `title`, a `purpose` and a `subject` written when
   * the design was drawn, and the shell used to render them over whatever the
   * backend had actually answered. On `cust-360` that meant a customer id which
   * resolves to nothing still drew a person's name — "Lina Vogt", registered
   * 2025-11-04, language de-DE — in the largest type on the page, because those
   * words are literals in `screens/customers.ts` and no read had to succeed for
   * them to appear.
   *
   * The fallback is the NAV LABEL, which is a fact about where the operator is
   * standing rather than a claim about what is in front of them. A loader that
   * knows the subject says so; one that does not leaves the heading generic,
   * which is the honest shape of "this is the Customer 360 screen and the read
   * has not told us whose".
   */
  const title = answer?.data.title ?? labelOf(screenId)
  const purpose = answer?.data.purpose ?? ''
  const subject = answer?.data.subject ?? labelOf(screenId)

  /**
   * The inspector panel, which a live screen may NOT inherit from the corpus.
   *
   * `DEFAULT_INSP`'s first tab is titled "Provenance" and states an invented
   * store and an invented read time; the tabs behind it carry an invented
   * customer, IBAN, `decision_ref` and provider event id. Beside a header chip
   * reading "Live", that panel is the most quotable fiction in the console —
   * a read timestamp and a decision reference are precisely the two things an
   * operator copies out and repeats to someone.
   *
   * So a live screen gets a panel built from what it actually knows: a real
   * Provenance tab, and three that say they are not wired. See
   * `lib/data/inspector`.
   */
  const inspectorSections = isLive
    ? liveInspector(provenance)
    : (screen.inspector ?? DEFAULT_INSP)

  const rowCount = useMemo(
    () => blocks.find((b) => b.t === 'table')?.rows.length ?? 0,
    [blocks],
  )

  // Verbatim from the prototype's keydown handler. Queues have to be workable
  // without the mouse — an operator clearing a review queue does it with j/k
  // and Enter, not by aiming at 23px rows.
  useEffect(() => {
    let gPressed = false
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName ?? ''
      const typing = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen(true)
        setQuery('')
        return
      }
      if (e.key === 'Escape') {
        setPaletteOpen(false)
        setHelpOpen(false)
        return
      }
      if (typing) return

      if (e.key === 'j') {
        e.preventDefault()
        setCursor((c) => Math.min(rowCount - 1, c + 1))
      } else if (e.key === 'k') {
        e.preventDefault()
        setCursor((c) => Math.max(0, c - 1))
      } else if (e.key === 'Enter') {
        setInspOpen(true)
        flash(`Row ${cursor + 1} loaded into inspector`)
      } else if (e.key === '?') {
        setHelpOpen((h) => !h)
      } else if (e.key === 't') {
        setTheme((t) => (t === 'dark' ? 'light' : 'dark'))
      } else if (e.key === 'd') {
        if (gPressed) go('dashboard')
        else setDensity((d) => (d === 34 ? 44 : 34))
      } else if (e.key === '[') {
        setNavCollapsed((c) => !c)
      } else if (e.key === ']') {
        setInspOpen((o) => !o)
      } else if (e.key >= '1' && e.key <= '5') {
        const states: ScreenState[] = ['populated', 'loading', 'empty', 'error', 'denied']
        const next = states[Number(e.key) - 1]
        if (next !== undefined) setView(next)
      }
      gPressed = e.key === 'g'
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [cursor, flash, go, rowCount])

  const reportedCell = session.status === 'signed-in' ? session.session.cell : null
  const cellRole: CellRole = cellPreview ?? reportedCell?.role ?? config.cellRole
  const cellId = reportedCell?.id ?? config.cellId

  /**
   * The cell id to DRAW, which differs from `cellId` only under a preview.
   *
   * A previewed standby names a cell nobody answered from, so it borrows the
   * design's other cell id rather than relabelling the real one — an operator
   * previewing standby must not read the primary's id beside the word STANDBY.
   * A cell that is standby because the BACKEND said so keeps its own id: that
   * banner states `WALLET_CELL_ID=` and a fixture id there would be a fabricated
   * fact about the deployment, on the banner that exists to say where you are.
   */
  const shownCellId = cellPreview === 'standby' ? 'eu-west-1b' : cellId

  /**
   * What the configured cell and the answering cell disagree about.
   *
   * Neither side wins. A console started against `eu-central-1a · primary` that
   * is being answered by a different cell is either pointed at the wrong
   * environment or running an overlay somebody edited by hand, and displaying
   * one of the two quietly would hide whichever is the incident.
   *
   * Only computed once a backend has actually answered — before that there is
   * one claim and nothing to disagree with it.
   */
  const cellMismatch =
    reportedCell === null ||
    (reportedCell.id === config.cellId && reportedCell.role === config.cellRole)
      ? null
      : `cell mismatch — configured ${config.cellId}/${config.cellRole}, answered by ${reportedCell.id}/${reportedCell.role}`

  const standby = cellRole === 'standby'

  /**
   * The roles this session actually holds, or `null` while we do not know.
   *
   * `null` is not "holds nothing": a backend that has not answered yet, or one
   * without `/v1/admin/me`, has told us nothing about authority, and drawing a
   * console as though every screen were refused would be an answer we do not
   * have. Unknown falls back to the design's own behaviour — the corpus, whole,
   * with the switcher previewing each role.
   */
  const heldRoles: readonly string[] | null =
    session.status === 'signed-in' ? session.session.roles : null

  /**
   * The role the console is ACTING AS, and where it came from.
   *
   * First a role the operator asked to preview; then the first role the session
   * holds, so an operator's own view is what they see on arrival and the
   * switcher's value is always one of its options; then the design's default,
   * for a console with no session to be anybody in.
   */
  const role = rolePreview ?? ownRole(heldRoles) ?? 'Ops'

  /**
   * Whether the DRAWING is scoped to this operator.
   *
   * Two rules, and the second is the one that matters:
   *
   *  1. The screen's declared roles are matched against the acting role, as
   *     before — that is the design's own gate and it is what makes the
   *     switcher a useful preview of what each role sees.
   *
   *  2. **A preview may narrow and never widen.** Previewing Admin on a session
   *     holding support must not reveal a screen support cannot see. So a
   *     preview is intersected with what the session actually holds; a session
   *     whose roles are unknown has nothing to intersect with and the preview
   *     stands alone, which is the corpus behaviour this console had before
   *     anyone could sign in.
   *
   * None of this is enforcement. go-wallet decides on every request whether or
   * not the console drew the button (Part I §5), and the rule below deliberately
   * does not apply to a screen the backend has already answered — see
   * `effectiveView`.
   */
  const previewAllowed = holdsAnyRole([role], screen.roles)
  const sessionAllowed = heldRoles === null || holdsAnyRole(heldRoles, screen.roles)
  const allowed = previewAllowed && sessionAllowed

  const refusal = answer?.refusal ?? null

  /**
   * The decision row behind a refusal, resolved.
   *
   * Keyed off the refusal and nothing else, so previewing Denied on a wired
   * screen issues no request — there is no reference to resolve, and a lookup
   * fired for a refusal that did not happen would put a real backend answer
   * under a drawn panel.
   *
   * It goes through `api` rather than the watched client `readScreen` builds:
   * this read's own 403 is a fact about the explorer's permissions, not a
   * second refusal of the screen, and folding it back into `refusal` would let
   * a failed lookup rewrite the reference the operator is being handed.
   */
  const lookupRef = refusal === null ? null : refusal.decisionRef
  const decisionRead = useQuery({
    queryKey: ['decision', lookupRef],
    queryFn: () => {
      if (lookupRef === null) throw new Error('no decision reference to look up')
      return readDecision(api, lookupRef)
    },
    enabled: lookupRef !== null,
    // `readDecision` resolves for every answer the explorer gives, so a
    // rejection is an abort and retrying it would only delay the panel.
    retry: false,
  })

  /**
   * What the panel may say about the row — `undefined` on a preview, which is
   * what keeps the drawing intact.
   *
   * A refusal that arrived without a reference gets `no_ref` rather than a
   * failed lookup: there was nothing to look up, and reporting that as a failed
   * check would describe a request nobody made.
   */
  const decisionCheck: DecisionCheck | undefined =
    refusal === null
      ? undefined
      : lookupRef === null
        ? { kind: 'no_ref' }
        : (decisionRead.data ??
            (decisionRead.isError
              ? {
                  kind: 'unverified',
                  ref: lookupRef,
                  reason: `the lookup did not complete — ${readFailure(decisionRead.error)}`,
                }
              : { kind: 'checking' }))

  /**
   * The state a live screen is ACTUALLY in, or `null` when it is genuinely
   * populated.
   *
   * The four non-populated views were designed states before anything was
   * wired, and the header's state buttons still preview them — that is how a
   * reviewer looks at the empty copy for a screen whose queue happens not to be
   * empty today. The two coexist under one rule: **reality can only ever move
   * the screen away from `populated`, never toward it.** A preview may show
   * less than there is (skeletons over a screen that has loaded); nothing can
   * make a screen claim to be populated while its read is pending, refused,
   * failed or empty.
   */
  const liveView: ScreenState | null = !isLive
    ? null
    : screenRead.isPending
      ? 'loading'
      : screenRead.isError || answer === null
        ? 'error'
        : refusal !== null
          ? 'denied'
          : answer.data.blocks.length === 0
            ? 'empty'
            : null

  /**
   * The view, with authority resolved.
   *
   * **A screen the backend answered is not hidden by a guess.** When a wired
   * screen reads successfully `liveView` is `null`, and the console used to let
   * a picked role decide from there — drawing the Denied panel over data the
   * server chose to send, with required roles taken from the design corpus
   * about a refusal that never happened.
   *
   * The server is the authority (Part I §5). If it answered, the operator may
   * see it; if it refused, `liveView` is already `denied` and the panel names
   * the role the refusal itself named. Either way the local gate has nothing to
   * add, and this is also what keeps the console out of the business of
   * modelling role containment: a Compliance session reading an Ops-scoped
   * screen is allowed by the server, so it is allowed here.
   *
   * A corpus screen has no backend answer to defer to, so the gate stands —
   * there it is a drawing of what a role sees, which is what it was always for.
   */
  const effectiveView: ScreenState =
    liveView ?? (!allowed && !isLive && view === 'populated' ? 'denied' : view)
  const writeBlocked = standby || !allowed

  /**
   * What the selected row opens into, and the key it opens.
   *
   * The RULE lives here and the destinations live in `openers.ts`. It used to
   * be one hard-coded branch for `rk-aml`, which was right for one screen and
   * wrong as a shape: the second openable screen would have restated this rule
   * with a chance to get it wrong.
   *
   * Keyed off `rowKeys` rather than off the row's rendered first cell, because
   * that cell renders things like `— no reference` for a row whose identity did
   * not arrive, and a request built from it would report whatever answered as
   * that operator's record. A row with no identity opens into nothing.
   *
   * Gated on `liveView === null` — a genuinely populated read. Previewing the
   * loading, empty, error or denied view is a preview of the drawing, and
   * firing a real read underneath one would put real data below a drawn state.
   */
  const opener = openerFor(screenId)
  const openKey =
    opener !== undefined && liveView === null ? (answer?.data.rowKeys?.[cursor] ?? null) : null

  const rowRead = useQuery({
    queryKey: ['row-open', screenId, openKey],
    queryFn: () => {
      if (opener === undefined || openKey === null) throw new Error('no row to open')
      return opener(api, openKey)
    },
    enabled: openKey !== null,
    // An opener resolves for every answer the backend gives, so a rejection
    // here is an abort, and retrying an abandoned navigation only delays the
    // record the operator actually asked for.
    retry: false,
  })

  /**
   * The case's own blocks, appended below the queue.
   *
   * Below rather than replacing: the queue is the screen, and swapping it for
   * one case would cost the operator the depth figure and the other proposals
   * every time they looked at one. It is also why these are kept out of
   * `blocks` — `rowCount` and the empty-screen test are about the QUEUE, and a
   * case's trail rows are not queue rows to move a cursor through.
   *
   * The in-flight state says so. Rendering nothing while the read is in the air
   * would draw a case with no trail, which is the one sentence about an
   * append-only record this console must never say by accident.
   */
  const openedBlocks: readonly BlockSpec[] = useMemo(() => {
    if (openKey === null) return []
    if (rowRead.data !== undefined) return rowRead.data.blocks
    if (rowRead.isError) {
      return [{
        t: 'note',
        tone: 'r',
        kicker: 'Row',
        title: `${openKey} was not opened`,
        body: `The read did not complete — ${readFailure(rowRead.error)}. Nothing below is known about this case, which is different from a case with an empty trail.`,
        ref: `Asked for: ${openKey}`,
      }]
    }
    return [{
      t: 'note',
      tone: 'n',
      kicker: 'Row',
      title: `Reading ${openKey}…`,
      body: 'The record this row opens into is being read. Nothing is shown until it arrives — an empty record and an unread one are different findings.',
      ref: `Asked for: ${openKey}`,
    }]
  }, [openKey, rowRead.data, rowRead.isError, rowRead.error])

  /**
   * The reference an operator quotes when they ask why they were refused.
   *
   * Only the first branch is a real one, and it is never back-filled: a backend
   * that refuses without a `decision_ref` has broken law 13, and printing a
   * plausible-looking reference over that would hide the breach rather than
   * report it. The other two branches are previews and say so, because a
   * fabricated reference an operator pastes into the decision explorer wastes
   * their time and teaches them the explorer is broken.
   */
  const decisionRef =
    refusal !== null
      ? (refusal.decisionRef ?? 'none — the backend refused without one (law 13)')
      : isLive
        ? 'not requested — this is a role preview, not a refusal'
        : `authz:2026-07-31:${(screenId.length * 7 + 41).toString(16)}c2`

  /**
   * "Checked" on the empty state. A live empty screen was checked at the moment
   * the backend read it; the corpus clock would date it to the design's instant,
   * which is a timestamp nobody produced.
   */
  const emptyClock =
    (provenance === null ? null : readStampOf(provenance)) ?? 'not stamped by the backend'

  // A disabled control with no reason beside it is the thing that generates
  // the support ticket. Say which of the two reasons it is, and when writing
  // IS available say where the record of it lands (invariant 6).
  /**
   * Whether the operator is looking at a role that is not their own.
   *
   * `heldRoles !== null` matters: without a session there is nothing to preview
   * AGAINST, and the switcher is the corpus's own control for looking at each
   * role's view. That is not a preview of somebody else, it is the design.
   */
  const previewing = rolePreview !== null && heldRoles !== null

  /**
   * The role being previewed, when the preview is what caused the refusal.
   *
   * `null` when the session itself lacks the scope — a different sentence with
   * a different next action. Telling somebody to request elevation for a role
   * they hold, because they changed a dropdown, is the same wrong advice #47
   * was about, pointed at a different person.
   */
  const previewRefusal = previewing && sessionAllowed && !previewAllowed ? role : null

  const writeNote = standby
    ? 'Disabled — this is the standby cell. Writes happen on the primary.'
    : !allowed
      ? `Disabled — requires ${(screen.roles ?? []).join(' or ')}. You are acting as ${role}.`
      : `Enabled for ${role} · lands in audit_log with actor, action, subject and reason code.`

  /*
   * No incidents, because there is no source (#124).
   *
   * This used to be `degraded ? DEGRADED_INCIDENTS : CALM_INCIDENTS` — two
   * fixture lists, one of them describing a HIGH-severity failure of a payment
   * rail at an institution this system is not even integrating. An operator
   * can act on that, which makes it the most dangerous thing the demo harness
   * carried.
   *
   * The drawer stays: it is the right shape for real incidents. It renders
   * empty until an endpoint answers, and an empty drawer is the honest state —
   * the console has not been told about any incident, which is different from
   * asserting there are none.
   */
  const incidents: readonly Incident[] = []

  // A degraded provider means analytics is behind. The note goes ABOVE the
  // figures it qualifies, not below them, because a caveat read after the
  // number has already been read is a caveat that did not work.
  const showsAnalytics =
    screen.source === 'analytics' || blocks.some((b) => 'src' in b && b.src === 'analytics')
  const prefix: readonly NoteBlock[] =
    degraded && showsAnalytics ? [{ t: 'note', ...DEGRADED_NOTE }] : []

  const paletteGroups = useMemo(
    () => buildPalette(query, go, flash, () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))),
    [query, go, flash],
  )
  const needle = (query.trim().startsWith('>') ? query.trim().slice(1) : query.trim())
    .toLowerCase()
  const noMatch = needle.length > 2 && !paletteGroups.some((g) => g.items.length > 0)

  return (
    <div
      data-k={theme}
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--k-bg)',
        color: 'var(--k-ink)',
        fontSize: '13px',
        lineHeight: 1.45,
        overflow: 'hidden',
      }}
    >
      <CellBar
        cellId={shownCellId}
        cellRole={cellRole}
        cellSource={reportedCell === null ? 'config' : 'backend'}
        cellMismatch={cellMismatch}
        environment={environment}
        incidents={incidents}
        incidentsOpen={incidentsOpen}
        clock={(provenance === null ? null : readStampOf(provenance)) ?? ''}
        onToggleCell={() => setCellPreview(standby ? 'primary' : 'standby')}
        onToggleEnvironment={() =>
          setEnvironment((e) => (e === 'production' ? 'staging' : 'production'))
        }
        onToggleIncidents={() => setIncidentsOpen((o) => !o)}
      />
      {incidentsOpen && <IncidentDrawer incidents={incidents} />}

      <MastHead
        /* #200: the masthead says so when a preview cannot reach this screen.
           `isLive` -- the screen has a loader -- is the condition, because that
           is the term `effectiveView` gates the local role rule on: it applies
           the rule only when `!isLive`, so on ANY wired screen the preview
           cannot change the panel, whatever the read did.

           This was `liveView !== null`, which is not that and is not what its
           own comment claimed either. `liveView` is `null` for a SUCCESSFUL
           read (see its definition) -- so the note was hidden in exactly the
           case it exists for, the everyday one where a wired screen renders
           data and a picked role changes nothing. And it was SHOWN while a
           read was merely in flight, then vanished when the data arrived,
           which is the opposite of the truth in both directions (#221). */
        screenIsServerAnswered={isLive}
        role={role}
        onRole={(r) => {
          setRolePreview(r)
          setView('populated')
        }}
        density={density}
        onToggleDensity={() => setDensity((d) => (d === 34 ? 44 : 34))}
        theme={theme}
        onToggleTheme={() => setTheme((t) => (t === 'light' ? 'dark' : 'light'))}
        onOpenPalette={() => {
          setPaletteOpen(true)
          setQuery('')
        }}
        onToggleHelp={() => setHelpOpen((h) => !h)}
        onHome={() => go('dashboard')}
      />

      {standby && <StandbyBanner cellId={shownCellId} />}
      {breakGlass && <BreakGlassBanner onEnd={() => setBreakGlass(false)} />}

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <NavRail
          screen={screenId}
          collapsed={navCollapsed}
          open={openSections}
          onToggleSection={(label) =>
            setOpenSections((prev) => {
              const si = NAV.findIndex((s) => s[0] === label)
              const holds = NAV[si]?.[2].some((i) => i[0] === screenId) ?? false
              const current = prev[label] ?? (holds || si < 7)
              return { ...prev, [label]: !current }
            })
          }
          onNavigate={go}
          onToggleCollapsed={() => setNavCollapsed((c) => !c)}
        />

        <main
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <ScreenHeader
            screenId={screenId}
            title={title}
            purpose={purpose}
            provenance={provenance}
          />

          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px 40px' }}>
            {effectiveView === 'denied' && (
              <DeniedView
                role={role}
                // The refusal an operator caused themselves, by looking at
                // another role. It gets its own sentence and its own next
                // action, which is the dropdown rather than an Admin.
                previewedRole={previewRefusal}
                requiredRoles={(screen.roles ?? ROLES).join(' or ')}
                // What the refusal itself named, never the screen's declared
                // scope. Only a real refusal has one; a preview passes nothing
                // and keeps the drawing.
                statedRole={refusal === null ? null : refusal.requiredRole}
                decisionRef={decisionRef}
                // Keyed off the actual refusal, not off the screen being live.
                // Previewing Denied on a wired screen is still a preview of the
                // drawing — same rule as ErrorView.
                live={refusal !== null}
                check={decisionCheck}
              />
            )}
            {effectiveView === 'loading' && <LoadingView />}
            {effectiveView === 'error' && (
              <ErrorView
                // Only a REAL failure supplies the detail, and supplying it is
                // what suppresses the designed example — the named timeout,
                // customer 1042, the four-minute-old provider snapshot, the
                // trace id. That example is the design on a corpus screen and a
                // fabrication over a live read that actually broke, in the one
                // place an operator looks to find out what broke. Keyed off
                // `liveView`, not `isLive`: previewing Error on a live screen is
                // still a preview of the drawing.
                {...(liveView === 'error' ? { detail: readFailure(screenRead.error) } : {})}
                onRetry={
                  // A live screen retries the read. A corpus screen has nothing
                  // to re-read, so its retry dismisses the preview.
                  isLive
                    ? () => {
                        void screenRead.refetch()
                      }
                    : () => setView('populated')
                }
              />
            )}
            {effectiveView === 'empty' && <EmptyView clock={emptyClock} />}

            {effectiveView === 'populated' && (
              <div>

                {[...prefix, ...blocks].map((block, i) => (
                  <Block
                    // Blocks are an ordered list in the spec with no ids; their
                    // order is the design's and does not change at runtime.
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${screenId}-${tab}-${i}`}
                    block={block}
                    query={query}
                    onQuery={setQuery}
                    cursor={cursor}
                    onRowClick={(r) => {
                      setCursor(r)
                      setInspOpen(true)
                    }}
                    facetsOn={facetsOn}
                    onToggleFacet={(k) =>
                      setFacetsOn((prev) => {
                        const next = new Set(prev)
                        if (next.has(k)) next.delete(k)
                        else next.add(k)
                        return next
                      })
                    }
                    writeBlocked={writeBlocked}
                    writeNote={writeNote}
                    onNavigate={go}
                    onFlash={flash}
                  />
                ))}

                {/* What the selected row opens into. Its rows carry no cursor:
                    the screen's own table owns the cursor, and an opened record
                    is something to read rather than a list to work through.

                    This used to hardcode `writeBlocked` and say "this console
                    reads what a row opens into and does not act on it", which
                    was true of every opener until #111 and is not any more: the
                    impersonation review queue opens into the claim that held a
                    name AND the one decision an operator may make about it. So
                    the opened blocks now inherit the screen's own write posture
                    — a standby cell disables them exactly as it disables the
                    screen's own controls — and every other opener stays
                    read-only because it composes no control, which is a property
                    of the adapter rather than of a flag set here.

                    The permission that gates the one control is NOT this flag.
                    It is resolved by the adapter against the closure the backend
                    reports, and it travels on the block. */}
                {openedBlocks.map((block, i) => (
                  <Block
                    // Same reason the screen's own blocks use their index: an
                    // opened record's blocks are an ordered list with no ids,
                    // and the order is the composer's rather than anything that
                    // moves.
                    // eslint-disable-next-line react/no-array-index-key
                    key={`open-${openKey ?? ''}-${i}`}
                    block={block}
                    query={query}
                    onQuery={setQuery}
                    cursor={-1}
                    onRowClick={() => undefined}
                    facetsOn={facetsOn}
                    onToggleFacet={() => undefined}
                    writeBlocked={writeBlocked}
                    writeNote={writeNote}
                    onNavigate={go}
                    onFlash={flash}
                  />
                ))}
              </div>
            )}
          </div>
        </main>

        <Inspector
          open={inspOpen}
          subject={subject}
          tab={inspTab}
          onTab={setInspTab}
          sections={inspectorSections}
          rawOpen={rawOpen}
          onToggleRaw={() => setRawOpen((o) => !o)}
          onToggle={() => setInspOpen((o) => !o)}
        />
      </div>

      {paletteOpen && (
        <Palette
          query={query}
          onQuery={setQuery}
          groups={paletteGroups}
          noMatch={noMatch}
          onClose={() => setPaletteOpen(false)}
        />
      )}
      {helpOpen && <HelpOverlay onClose={() => setHelpOpen(false)} />}
      {toast !== '' && <Toast message={toast} />}
    </div>
  )
}

/**
 * What actually failed, in one line, for a live screen's error state.
 *
 * A loader resolves for every answer the backend gives — including "I could not
 * read that" — so reaching the error state means the read produced no answer at
 * all: an abort, or a bug in the adapter. Either way the operator is given the
 * real name of it. The alternative is the designed example, which describes an
 * incident that did not happen.
 */
function readFailure(error: unknown): string {
  // Read off the shape rather than through `instanceof`. A `DOMException` from
  // an aborted fetch is the commonest thing to land here and it does not
  // reliably answer to the `Error` this bundle closed over — different realm,
  // same duck — and silently degrading a real abort to "no reason" would put
  // the operator back where the designed example left them.
  if (typeof error === 'object' && error !== null) {
    const shape = error as { readonly name?: unknown; readonly message?: unknown }
    const name = typeof shape.name === 'string' ? shape.name : ''
    const message = typeof shape.message === 'string' ? shape.message : ''
    if (name !== '' && message !== '') return `${name}: ${message}`
    if (name !== '' || message !== '') return name === '' ? message : name
  }
  return 'The read did not complete and reported no reason.'
}

/**
 * Palette results. Transcribed from the prototype's `renderVals`.
 *
 * # There is no Entities group, and that is the honest state (#30)
 *
 * The design ranks entities above screens, because an operator who pastes an
 * IBAN wants that account and not the screen whose name happens to contain
 * those letters. That ranking is right and it comes back the day the console
 * can resolve an identifier — over `GET /v1/admin/search`, which go-wallet
 * does not have.
 *
 * What it must not come back from is `ENTITIES` in `fixtures.ts`, which is why
 * that constant is gone. Those rows carried a `customers.id`, an email and an
 * IBAN and were returned as answers to a search. A screen hit is a navigation
 * suggestion and cannot be mistaken for a record; an entity hit is a claim
 * that the record exists.
 */
function buildPalette(
  raw: string,
  go: (id: string) => void,
  flash: (m: string) => void,
  toggleTheme: () => void,
): PaletteGroup[] {
  const q = raw.trim()
  const commandMode = q.startsWith('>')
  const needle = (commandMode ? q.slice(1) : q).toLowerCase()

  const navHits: PaletteHit[] = REACHABLE_NAV.flatMap(([section, , items]) =>
    items
      .filter(
        ([, label]) =>
          needle === '' ||
          label.toLowerCase().includes(needle) ||
          section.toLowerCase().includes(needle),
      )
      .map(([id, label]) => ({
        label,
        match: section,
        meta: '',
        dim: false,
        go: () => go(id),
      })),
  )

  /*
   * Only commands that reach somewhere (#124).
   *
   * The list carried two kinds that no longer belong. A command targeting a
   * screen hidden by #115 offers a destination that is not in the rail, which
   * is the rail's rule broken by another route in. And a command listed with a
   * blocked-reason — "needs the editor and its diff" — is #121's content in a
   * palette: a control somebody has not built, described to the person who
   * wanted to use it.
   */
  const commandHits: PaletteHit[] = COMMANDS.filter(
    ([label, target, blocked]) =>
      blocked === ''
      && (target === null || isReachable(target))
      && (needle === '' || label.toLowerCase().includes(needle)),
  ).map(([label, target, blocked]) => ({
    label,
    match: blocked,
    meta: target !== null ? 'go' : blocked !== '' ? 'blocked' : 'action',
    dim: blocked !== '',
    go: () => {
      if (target !== null) go(target)
      else if (blocked !== '') flash(`Not reachable from the palette — ${blocked}`)
      else toggleTheme()
    },
  }))

  if (commandMode) return [{ label: 'Commands', items: commandHits }]

  const groups: PaletteGroup[] = []
  // The Entities group went here, above Screens. Restore it from
  // `GET /v1/admin/search` when that endpoint exists — not from a fixture.
  if (navHits.length > 0) groups.push({ label: 'Screens', items: navHits.slice(0, 8) })
  if (needle === '') groups.push({ label: 'Commands', items: commandHits.slice(0, 4) })
  return groups
}

/**
 * The corpus label for a role this session holds, narrowest first.
 *
 * Two jobs. It bridges the casing — the design writes `Ops`, the directory
 * writes `ops`, and the switcher's value has to be one of its own options or
 * every gate compares against a role nobody holds. And it picks in `ROLES`
 * order, which runs from Support outwards, so an operator holding several roles
 * arrives in the NARROWEST of them: the widest view is a click away and is
 * chosen, rather than being what somebody gets by opening the console.
 */
function ownRole(held: readonly string[] | null): string | undefined {
  if (held === null) return undefined
  return ROLES.find((r) => held.some((h) => h.toLowerCase() === r.toLowerCase()))
}
